import { computed, effect, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';

import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { from } from 'rxjs';
import {
  AccettaCondivisioneGQL,
  Admin__Type_Referral_Enum,
  Admin_Consenso_Privacy_Constraint,
  Admin_Consenso_Privacy_Insert_Input,
  Admin_Consenso_Privacy_Update_Column,
  GetCodiceOperatoreGQL,
  GetPrivacyGQL,
  GetProfileGQL,
  GetProfileQuery,
  GetReferralGQL,
  InsertOrUpdateProfileGQL,
  Profilo_Insert_Input,
  Profilo_On_Conflict,
  Scalars,
  SetOrUpdatePrivacyGQL,
  _Tipologia_Profilo_Enum,
  UseReferralGQL,
  Energy_Village__Stato_Sottoscrizione_Enum,
  SearchSottoscrizioniSegnalatoreDocument,
  SearchSottoscrizioniSegnalatoreSubscription,
  SearchSottoscrizioniSegnalatoreCountDocument,
  SearchSottoscrizioniAmicoDocument,
  SearchSottoscrizioniAmicoSubscription,
  GetAmiciPresentatiGQL,
  GetAmiciPresentatiCountGQL,
  GetAmiciPresentatiCountSubscriptionDocument,
} from '../../../_core/_graphql/generated';
import { initialUserState } from './user.state';
import { LoadingService } from '../../../_core/_services/loading.service';
import { AuthService } from '../../../_core/_services/auth.service';
import { ApolloRoleService } from '../../../_core/_services/apollo-role.service';
import { LoghiStore } from '../../../_core/_signal-store/_loghi/loghi.store';


const emptySearchArgs = {
  p_search: null,
  p_stato: null,
  p_richiesta: null,
  p_cabina: null,
  p_configurazioni: null,
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withState(initialUserState),
  withComputed((store) => ({
    rete_sottoscrizioni_lista_active_filter_count: computed(() => {
      const filter = store.rete_sottoscrizioni_lista_active_filter?.();
      const search = store.rete_sottoscrizioni_lista_search();
      let count = 0;
      if (filter?.configurazioni?.length) count += filter.configurazioni.length;
      if (filter?.cabina?.length) count += filter.cabina.length;
      if (filter?.stato?.length) count += filter.stato.length;
      if (filter?.richiesta?.length) count += filter.richiesta.length;
      if (filter?.nascondi?.length) count += 1;
      if (search?.trim()) count += 1;
      return count;
    }),
    rete_sottoscrizioni_aggregato_active_filter_count: computed(() => {
      const filter = store.rete_sottoscrizioni_aggregato_active_filter?.();
      const search = store.rete_sottoscrizioni_aggregato_search();
      let count = 0;
      if (filter?.configurazioni?.length) count += filter.configurazioni.length;
      if (search?.trim()) count += 1;
      return count;
    }),
  })),

  withMethods((store, injector = inject(Injector)) => {
    const subs = new Map<string, { unsubscribe: () => void }>();

    const setSub = (key: string, sub: { unsubscribe: () => void }) => {
      subs.get(key)?.unsubscribe?.();
      subs.set(key, sub);
    };

    const clearSub = (key: string) => {
      subs.get(key)?.unsubscribe?.();
      subs.delete(key);
    };

    const clearAllSubs = (prefix?: string) => {
      for (const [k, s] of subs.entries()) {
        if (!prefix || k.startsWith(prefix)) {
          s?.unsubscribe?.();
          subs.delete(k);
        }
      }
    };

    const asText = (v: any): string | null => {
      const candidate =
        typeof v === 'string' ? v
          : typeof v?.key === 'string' ? v.key
            : null;
      if (!candidate) return null;
      const x = candidate.trim();
      return x.length > 0 ? x : null;
    };

    const toPgArrayText = (values: string[]): string | null => {
      if (!values || values.length === 0) return null;
      const escaped = values.map(
        (v) => `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`,
      );
      return `{${escaped.join(',')}}`;
    };

    const toPgArrayInt = (values: number[]): string | null =>
      values?.length ? `{${values.join(',')}}` : null;

    const asInt = (v: any): number | null => {
      const candidate =
        typeof v === 'number' ? v
          : typeof v === 'string' ? Number(v)
            : typeof v?.id === 'number' ? v.id
              : typeof v?.id === 'string' ? Number(v.id)
                : typeof v?.value === 'number' ? v.value
                  : typeof v?.value === 'string' ? Number(v.value)
                    : null;
      return Number.isInteger(candidate) && candidate !== null ? (candidate as number) : null;
    };

    const defaultNascondi = ['nascondi_recessi', 'nascondi_rifiutati'];
    const statiRecesso: string[] = [
      Energy_Village__Stato_Sottoscrizione_Enum.Recessa,
      Energy_Village__Stato_Sottoscrizione_Enum.RecessoRichiesto,
      Energy_Village__Stato_Sottoscrizione_Enum.DisdettaRichiesta,
    ];
    const statiRifiutati: string[] = [Energy_Village__Stato_Sottoscrizione_Enum.Rifiutata];

    const buildListaArgs = (filter?: any, search?: string) => {
      const p_configurazioni = (filter?.configurazioni ?? [])
        .map(asText)
        .filter((v: string | null): v is string => v != null);
      const p_cabina = (filter?.cabina ?? [])
        .map(asInt)
        .filter((v: number | null): v is number => v != null);
      let p_stato = (filter?.stato ?? [])
        .map(asText)
        .filter((v: string | null): v is string => v != null) as string[];
      const p_richiesta = (filter?.richiesta ?? [])
        .map(asText)
        .filter((v: string | null): v is string => v != null);

      const nascondi: string[] = filter?.nascondi ?? defaultNascondi;
      const nascondiRecessi = nascondi.includes('nascondi_recessi');
      const nascondiRifiutati = nascondi.includes('nascondi_rifiutati');
      const statiDaEscludere: string[] = [
        ...(nascondiRecessi ? statiRecesso : []),
        ...(nascondiRifiutati ? statiRifiutati : []),
      ];

      if (statiDaEscludere.length > 0) {
        if (p_stato.length > 0) {
          p_stato = p_stato.filter((s) => !statiDaEscludere.includes(s));
        } else {
          p_stato = Object.values(Energy_Village__Stato_Sottoscrizione_Enum).filter(
            (s) =>
              !statiDaEscludere.includes(s) &&
              s !== Energy_Village__Stato_Sottoscrizione_Enum.Cancellata,
          );
        }
      }

      return {
        p_search: search?.trim() ? search.trim() : null,
        p_stato: toPgArrayText(p_stato),
        p_richiesta: toPgArrayText(p_richiesta),
        p_cabina: toPgArrayInt(p_cabina),
        p_configurazioni: toPgArrayText(p_configurazioni),
      };
    };

    const buildAggregatoArgs = (filter?: any, search?: string) => {
      const p_configurazioni = (filter?.configurazioni ?? [])
        .map(asText)
        .filter((v: string | null): v is string => v != null);
      return {
        p_search: search?.trim() ? search.trim() : null,
        p_stato: null,
        p_richiesta: null,
        p_cabina: null,
        p_configurazioni: toPgArrayText(p_configurazioni),
      };
    };

    return {
      init: () =>
        runInInjectionContext(injector, () => {
          const auth = inject(AuthService);
          const useReferral = inject(UseReferralGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          patchState(store, { user_loading: true });

          try {
            const decoded = auth.getDecodedToken();
            if (!decoded) {
              patchState(store, { user_data: undefined, user_loading: false });
              return;
            }

            const referral_id = localStorage.getItem('ref');
            if (referral_id) {
              useReferral.mutate({ referral_id }).subscribe();
              localStorage.removeItem('ref');
            }

            // Salviamo nel nostro stato i dati utente base dal JWT
            patchState(store, {
              user_data: decoded,
              user_loading: false,
            });
          } catch (error) {
            patchState(store, {
              user_data: undefined,
              user_loading: false,
              failure: error,
            });
            _toast.add({
              key: 'global-toast',
              severity: 'error',
              summary: _translate.instant('toast.error.title'),
              detail: _translate.instant('toast.error.detail', { error }),
            });
          }
        }),
      setPrivacy: (objects: Admin_Consenso_Privacy_Insert_Input, silent = false) =>
        runInInjectionContext(injector, () => {
          const _mutation = inject(SetOrUpdatePrivacyGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          patchState(store, { privacy_loading: true });

          return _mutation
            .mutate(
              {
                objects,
                on_conflict: {
                  constraint:
                    Admin_Consenso_Privacy_Constraint.ConsensoPrivacyPkey,
                  update_columns: [
                    Admin_Consenso_Privacy_Update_Column.Newsletter,
                    Admin_Consenso_Privacy_Update_Column.ComunicazioniCommerciali,
                    Admin_Consenso_Privacy_Update_Column.SondaggiStatistici,
                  ],
                },
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  patchState(store, {
                    privacy_data:
                      res.data?.insert_admin_consenso_privacy?.returning,
                    privacy_loading: false,
                  });
                  if (!silent) {
                    _toast.add({
                      key: 'global-toast',
                      severity: 'success',
                      summary: _translate.instant(
                        'app.public.privacy_form.toast.success.summary',
                      ),
                      detail: _translate.instant(
                        'app.public.privacy_form.toast.success.detail',
                      ),
                    });
                  }
                  return res;
                },
                error: (error) => {
                  patchState(store, {
                    privacy_data: undefined,
                    privacy_loading: false,
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        }),
      getPrivacy: () =>
        runInInjectionContext(injector, () => {
          const getter = inject(GetPrivacyGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          patchState(store, { privacy_loading: true });

          return getter
            .fetch(
              {},
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  patchState(store, {
                    privacy_data: res.data?.admin_consenso_privacy,
                    privacy_loading: false,
                  });
                },
                error: (error) => {
                  patchState(store, {
                    privacy_data: undefined,
                    privacy_loading: false,
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        }),
      setProfile: (
        data: Profilo_Insert_Input,
        on_conflict?: Profilo_On_Conflict,
      ) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(InsertOrUpdateProfileGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          _loading.start();
          return _mutation
            .mutate(
              {
                objects: data,
                on_conflict,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  _loading.stop();

                  patchState(store, {
                    ...(res.data?.insert_profilo?.returning?.[0].tipologia ==
                      _Tipologia_Profilo_Enum.Prosumer
                      ? {
                        prosumer_profile_data:
                          res.data?.insert_profilo?.returning?.[0] ?? 'NO',
                        prosumer_profile_loading: false,
                      }
                      : {}),
                    ...(res.data?.insert_profilo?.returning?.[0].tipologia ==
                      _Tipologia_Profilo_Enum.Founder
                      ? {
                        founder_profile_data:
                          res.data?.insert_profilo?.returning?.[0] ?? 'NO',
                        founder_profile_loading: false,
                      }
                      : {}),
                    ...(res.data?.insert_profilo?.returning?.[0].tipologia ==
                      _Tipologia_Profilo_Enum.Installatore
                      ? {
                        installatore_profile_data:
                          res.data?.insert_profilo?.returning?.[0] ?? 'NO',
                        installatore_profile_loading: false,
                      }
                      : {}),
                  });

                  if (!data.hasOwnProperty('show_accesso_pro')) {
                    _toast.add({
                      key: 'global-toast',
                      severity: 'success',
                      summary: _translate.instant(
                        'app.private.profiling.profile.toast.success.summary',
                        {
                          type: 'edit',
                        },
                      ),
                      detail: _translate.instant(
                        'app.private.profiling.profile.toast.success.detail',
                        {
                          type: 'edit',
                          typeProfile: data.tipologia_profilo,
                        },
                      ),
                    });
                  }
                  return res.data?.insert_profilo?.returning[0];
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      setType: (type: _Tipologia_Profilo_Enum) => {
        runInInjectionContext(injector, async () => {
          localStorage.setItem('user', type);
          patchState(store, { type });
        });
      },
      getReferral: () =>
        runInInjectionContext(injector, () => {
          const getter = inject(GetReferralGQL);
          return getter.mutate({
            type: Admin__Type_Referral_Enum.Utente,
          });
        }),
      accettaCondivisione: (id: Scalars['uuid']['input']) =>
        runInInjectionContext(injector, () => {
          const mutation = inject(AccettaCondivisioneGQL);
          return mutation.mutate(
            {
              id,
            },
            {
              context: {
                headers: { 'x-hasura-role': 'user' },
              },
            },
          );
        }),
      initReteSottoscrizioni: (filter?: any, search?: string) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('segnalatore');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const resolvedFilter =
            filter !== undefined
              ? filter
              : store.rete_sottoscrizioni_lista_active_filter?.();
          const resolvedSearch =
            search !== undefined
              ? search
              : store.rete_sottoscrizioni_lista_search();

          const pageSize = store.rete_sottoscrizioni_lista_page_size();

          patchState(store, {
            rete_sottoscrizioni_lista_loading: true,
            rete_sottoscrizioni_lista_page: 0,
            rete_sottoscrizioni_lista_total: 0,
            rete_sottoscrizioni_lista_search: resolvedSearch ?? '',
            rete_sottoscrizioni_lista_active_filter: resolvedFilter,
          });

          clearSub('reteSottoscrizioniListCount');
          clearSub('reteSottoscrizioniList');

          const searchArgs = buildListaArgs(resolvedFilter, resolvedSearch);

          const countSub = _getter
            .subscribe<any>({
              query: SearchSottoscrizioniSegnalatoreCountDocument,
              variables: { args: searchArgs },
            })
            .subscribe({
              next: (res: any) => {
                patchState(store, {
                  rete_sottoscrizioni_lista_total:
                    res.data?.search_sottoscrizioni_aggregate?.aggregate?.count ?? 0,
                });
              },
              error: (error: any) => {
                patchState(store, { failure: error });
              },
            });
          setSub('reteSottoscrizioniListCount', countSub);

          const orderBy = store.rete_sottoscrizioni_lista_order_by();

          const sub = _getter
            .subscribe<SearchSottoscrizioniSegnalatoreSubscription>({
              query: SearchSottoscrizioniSegnalatoreDocument,
              variables: { args: searchArgs, limit: pageSize, offset: 0, order_by: orderBy },
            })
            .subscribe({
              next: (res: any) => {
                res?.data?.search_sottoscrizioni?.map((s: any) => {
                  _loghi.getLogo(s.cer_cabina?.cer?.logo?.id);
                  _loghi.getLogo(s.cer_cabina?.cer?.cer_padre?.logo?.id);
                });
                patchState(store, {
                  rete_sottoscrizioni_lista_data: res.data?.search_sottoscrizioni,
                });
                setTimeout(() => {
                  patchState(store, {
                    rete_sottoscrizioni_lista_loading: false,
                  });
                }, 100);
              },
              error: (error: any) => {
                patchState(store, {
                  rete_sottoscrizioni_lista_loading: false,
                  rete_sottoscrizioni_lista_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('reteSottoscrizioniList', sub);
          return sub;
        }),

      changePageReteSottoscrizioniLista: (
        page: number,
        requestedPageSize?: number,
      ) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('segnalatore');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const pageSize =
            requestedPageSize ?? store.rete_sottoscrizioni_lista_page_size();
          const offset = page * pageSize;
          const search = store.rete_sottoscrizioni_lista_search();
          const filter = store.rete_sottoscrizioni_lista_active_filter?.();

          patchState(store, {
            rete_sottoscrizioni_lista_page: page,
            rete_sottoscrizioni_lista_page_size: pageSize,
            rete_sottoscrizioni_lista_loading: true,
          });

          clearSub('reteSottoscrizioniList');

          const orderBy = store.rete_sottoscrizioni_lista_order_by();

          const sub = _getter
            .subscribe<SearchSottoscrizioniSegnalatoreSubscription>({
              query: SearchSottoscrizioniSegnalatoreDocument,
              variables: {
                args: buildListaArgs(filter, search),
                limit: pageSize,
                offset,
                order_by: orderBy,
              },
            })
            .subscribe({
              next: (res: any) => {
                res?.data?.search_sottoscrizioni?.map((s: any) => {
                  _loghi.getLogo(s.cer_cabina?.cer?.logo?.id);
                  _loghi.getLogo(s.cer_cabina?.cer?.cer_padre?.logo?.id);
                });
                patchState(store, {
                  rete_sottoscrizioni_lista_data: res.data?.search_sottoscrizioni,
                });
                setTimeout(() => {
                  patchState(store, {
                    rete_sottoscrizioni_lista_loading: false,
                  });
                }, 100);
              },
              error: (error: any) => {
                patchState(store, {
                  rete_sottoscrizioni_lista_loading: false,
                  rete_sottoscrizioni_lista_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('reteSottoscrizioniList', sub);
        }),

      changeSortReteSottoscrizioniLista: (order_by: any[]) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('segnalatore');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const pageSize = store.rete_sottoscrizioni_lista_page_size();
          const search = store.rete_sottoscrizioni_lista_search();
          const filter = store.rete_sottoscrizioni_lista_active_filter?.();

          patchState(store, {
            rete_sottoscrizioni_lista_order_by: order_by,
            rete_sottoscrizioni_lista_page: 0,
            rete_sottoscrizioni_lista_loading: true,
          });

          clearSub('reteSottoscrizioniList');

          const sub = _getter
            .subscribe<SearchSottoscrizioniSegnalatoreSubscription>({
              query: SearchSottoscrizioniSegnalatoreDocument,
              variables: {
                args: buildListaArgs(filter, search),
                limit: pageSize,
                offset: 0,
                order_by,
              },
            })
            .subscribe({
              next: (res: any) => {
                res?.data?.search_sottoscrizioni?.map((s: any) => {
                  _loghi.getLogo(s.cer_cabina?.cer?.logo?.id);
                  _loghi.getLogo(s.cer_cabina?.cer?.cer_padre?.logo?.id);
                });
                patchState(store, {
                  rete_sottoscrizioni_lista_data: res.data?.search_sottoscrizioni,
                });
                setTimeout(() => {
                  patchState(store, {
                    rete_sottoscrizioni_lista_loading: false,
                  });
                }, 100);
              },
              error: (error: any) => {
                patchState(store, {
                  rete_sottoscrizioni_lista_loading: false,
                  rete_sottoscrizioni_lista_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('reteSottoscrizioniList', sub);
        }),

      changePageReteSottoscrizioniAggregato: (
        page: number,
        requestedPageSize?: number,
      ) =>
        runInInjectionContext(injector, () => {
          const pageSize =
            requestedPageSize ??
            store.rete_sottoscrizioni_aggregato_page_size();
          patchState(store, {
            rete_sottoscrizioni_aggregato_page: page,
            rete_sottoscrizioni_aggregato_page_size: pageSize,
          });
        }),
      initReteSottoscrizioniAggregato: (filter?: any, search?: string) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('amico');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const resolvedFilter =
            filter !== undefined
              ? filter
              : store.rete_sottoscrizioni_aggregato_active_filter?.();
          const resolvedSearch =
            search !== undefined
              ? search
              : store.rete_sottoscrizioni_aggregato_search();

          patchState(store, {
            rete_sottoscrizioni_aggregato_loading: true,
            rete_sottoscrizioni_aggregato_search: resolvedSearch ?? '',
            rete_sottoscrizioni_aggregato_page: 0,
            rete_sottoscrizioni_aggregato_active_filter: resolvedFilter,
          });

          clearSub('reteSottoscrizioniAggregato');

          const sub = _getter
            .subscribe<SearchSottoscrizioniAmicoSubscription>({
              query: SearchSottoscrizioniAmicoDocument,
              variables: {
                args: buildAggregatoArgs(resolvedFilter, resolvedSearch),
              },
            })
            .subscribe({
              next: (res: any) => {
                patchState(store, {
                  rete_sottoscrizioni_aggregato_loading: false,

                  rete_sottoscrizioni_aggregato_data:
                    res.data?.search_sottoscrizioni.reduce(
                      (acc: any, item: any) => {
                        const cerId = item.cer_cabina.cer.id;

                        if (acc[cerId]) {
                          acc[cerId].count++;
                        } else {
                          _loghi.getLogo(item.cer_cabina?.cer?.logo?.id);
                          _loghi.getLogo(
                            item.cer_cabina?.cer?.cer_padre?.logo?.id,
                          );
                          acc[cerId] = {
                            cer: item.cer_cabina.cer,
                            count: 1,
                          };
                        }

                        return acc;
                      },
                      {} as Record<string, { cer: any; count: number }>,
                    ),
                });
              },
              error: (error: any) => {
                patchState(store, {
                  rete_sottoscrizioni_aggregato_loading: false,
                  rete_sottoscrizioni_aggregato_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('reteSottoscrizioniAggregato', sub);
          return sub;
        }),
      initReteSottoscrizioniConfigurazioni: () =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('amico');

          clearSub('reteSottoscrizioniConfigurazioni');

          const sub = _getter
            .subscribe<SearchSottoscrizioniAmicoSubscription>({
              query: SearchSottoscrizioniAmicoDocument,
              variables: { args: emptySearchArgs },
            })
            .subscribe({
              next: (res: any) => {
                const seen = new Set<string>();
                const configurazioni: Array<{
                  id: string;
                  alias: string;
                  cer_padre_alias: string | null;
                }> = [];
                for (const item of res.data?.search_sottoscrizioni ?? []) {
                  const cer = item.cer_cabina?.cer;
                  if (cer?.id && !seen.has(cer.id)) {
                    seen.add(cer.id);
                    configurazioni.push({
                      id: cer.id,
                      alias: cer.alias ?? cer.id,
                      cer_padre_alias: cer.cer_padre?.alias ?? null,
                    });
                  }
                }
                patchState(store, { rete_sottoscrizioni_configurazioni: configurazioni });
              },
              error: () => {
                patchState(store, { rete_sottoscrizioni_configurazioni: [] });
              },
            });

          setSub('reteSottoscrizioniConfigurazioni', sub);
        }),

      initReteAmiciPresentati: (search?: string) =>
        runInInjectionContext(injector, () => {
          const _getter = inject(GetAmiciPresentatiGQL);
          const _count = inject(GetAmiciPresentatiCountGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          const resolvedSearch =
            search !== undefined
              ? search
              : store.rete_amici_presentati_lista_search();
          const pageSize = store.rete_amici_presentati_lista_page_size();

          patchState(store, {
            rete_amici_presentati_lista_loading: true,
            rete_amici_presentati_lista_page: 0,
            rete_amici_presentati_lista_search: resolvedSearch ?? '',
          });

          clearSub('reteAmiciPresentatiList');

          const args = { p_search: resolvedSearch?.trim() ? resolvedSearch.trim() : null };
          const context = { context: { headers: { 'x-hasura-role': 'segnalatore' } } };
          const orderBy = store.rete_amici_presentati_lista_order_by();

          _count.fetch({ args }, context).subscribe({
            next: (res: any) => {
              patchState(store, {
                rete_amici_presentati_lista_total:
                  res.data?.search_user_entity_aggregate?.aggregate?.count ?? 0,
              });
            },
          });

          const sub = _getter
            .fetch(
              { args, limit: pageSize, offset: 0, order_by: orderBy },
              context,
            )
            .subscribe({
              next: (res: any) => {
                patchState(store, {
                  rete_amici_presentati_lista_loading: false,
                  rete_amici_presentati_lista_data: [...(res.data?.search_user_entity ?? [])],
                });
              },
              error: (error: any) => {
                patchState(store, {
                  rete_amici_presentati_lista_loading: false,
                  rete_amici_presentati_lista_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('reteAmiciPresentatiList', sub);
          return sub;
        }),

      changePageReteAmiciPresentatiLista: (
        page: number,
        requestedPageSize?: number,
      ) =>
        runInInjectionContext(injector, () => {
          const _getter = inject(GetAmiciPresentatiGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          const pageSize =
            requestedPageSize ?? store.rete_amici_presentati_lista_page_size();
          const offset = page * pageSize;
          const search = store.rete_amici_presentati_lista_search();
          const orderBy = store.rete_amici_presentati_lista_order_by();

          patchState(store, {
            rete_amici_presentati_lista_page: page,
            rete_amici_presentati_lista_page_size: pageSize,
            rete_amici_presentati_lista_loading: true,
          });

          clearSub('reteAmiciPresentatiList');

          const args = { p_search: search?.trim() ? search.trim() : null };

          const sub = _getter
            .fetch(
              { args, limit: pageSize, offset, order_by: orderBy },
              { context: { headers: { 'x-hasura-role': 'segnalatore' } } },
            )
            .subscribe({
              next: (res: any) => {
                patchState(store, {
                  rete_amici_presentati_lista_loading: false,
                  rete_amici_presentati_lista_data: [...(res.data?.search_user_entity ?? [])],
                });
              },
              error: (error: any) => {
                patchState(store, {
                  rete_amici_presentati_lista_loading: false,
                  rete_amici_presentati_lista_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('reteAmiciPresentatiList', sub);
        }),

      changeSortReteAmiciPresentatiLista: (orderBy: any[]) =>
        runInInjectionContext(injector, () => {
          const _getter = inject(GetAmiciPresentatiGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          patchState(store, {
            rete_amici_presentati_lista_order_by: orderBy,
            rete_amici_presentati_lista_page: 0,
            rete_amici_presentati_lista_loading: true,
          });

          clearSub('reteAmiciPresentatiList');

          const pageSize = store.rete_amici_presentati_lista_page_size();
          const search = store.rete_amici_presentati_lista_search();
          const args = { p_search: search?.trim() ? search.trim() : null };

          const sub = _getter
            .fetch(
              { args, limit: pageSize, offset: 0, order_by: orderBy },
              { context: { headers: { 'x-hasura-role': 'segnalatore' } } },
            )
            .subscribe({
              next: (res: any) => {
                patchState(store, {
                  rete_amici_presentati_lista_loading: false,
                  rete_amici_presentati_lista_data: [...(res.data?.search_user_entity ?? [])],
                });
              },
              error: (error: any) => {
                patchState(store, {
                  rete_amici_presentati_lista_loading: false,
                  rete_amici_presentati_lista_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('reteAmiciPresentatiList', sub);
        }),

      initReteAmiciPresentatiAggregato: () =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('amico');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          patchState(store, { rete_amici_presentati_aggregato_loading: true });

          clearSub('reteAmiciPresentatiAggregato');

          const args = { p_search: null };

          const sub = _getter
            .subscribe<any>({
              query: GetAmiciPresentatiCountSubscriptionDocument,
              variables: { args },
            })
            .subscribe({
              next: (res: any) => {
                const count = res.data?.search_user_entity_aggregate?.aggregate?.count ?? 0;
                patchState(store, {
                  rete_amici_presentati_aggregato_loading: false,
                  rete_amici_presentati_aggregato_data: count,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  rete_amici_presentati_aggregato_loading: false,
                  rete_amici_presentati_aggregato_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('reteAmiciPresentatiAggregato', sub);
          return sub;
        }),

      clearRete: () => {
        clearSub('reteSottoscrizioniList');
        clearSub('reteSottoscrizioniListCount');
        clearSub('reteSottoscrizioniAggregato');
        clearSub('reteSottoscrizioniConfigurazioni');
        clearSub('reteAmiciPresentatiList');
        clearSub('reteAmiciPresentatiAggregato');

        patchState(store, {
          rete_sottoscrizioni_configurazioni: undefined,
          rete_sottoscrizioni_lista_data: undefined,
          rete_sottoscrizioni_lista_loading: false,
          rete_sottoscrizioni_lista_page: 0,
          rete_sottoscrizioni_lista_total: 0,

          rete_sottoscrizioni_aggregato_data: undefined,
          rete_sottoscrizioni_aggregato_loading: false,
          rete_sottoscrizioni_aggregato_page: 0,

          rete_amici_presentati_lista_data: undefined,
          rete_amici_presentati_lista_loading: false,
          rete_amici_presentati_lista_page: 0,
          rete_amici_presentati_lista_total: 0,
          rete_amici_presentati_lista_search: '',

          rete_amici_presentati_aggregato_data: undefined,
          rete_amici_presentati_aggregato_loading: false,
        });
      },
    };
  }),
  withHooks({
    onInit(store) {
      const _toast = inject(MessageService);
      const _translate = inject(TranslateService);
      const _getterPrivacy = inject(GetPrivacyGQL);
      const _getterProfile = inject(GetProfileGQL);
      const _getterCodiceOperatore = inject(GetCodiceOperatoreGQL);

      // 🔥 Ripristiniamo l'ultimo profilo usato salvato in localStorage,
      // altrimenti lo store resterebbe bloccato sul valore iniziale hardcoded.
      const savedType = localStorage.getItem('user');
      if (
        savedType &&
        Object.values(_Tipologia_Profilo_Enum).includes(
          savedType as _Tipologia_Profilo_Enum,
        )
      ) {
        patchState(store, { type: savedType as _Tipologia_Profilo_Enum });
      }

      effect(() => {
        const user = store.user_data?.();
        if (user) {
          patchState(store, { codice_operatore_loading: true });
          _getterCodiceOperatore
            .fetch(
              {},
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .subscribe({
              next: (res) => {
                patchState(store, {
                  codice_operatore: res.data?.codice_operatore?.[0]?.codice,
                  codice_operatore_loading: false,
                });
              },
              error: (error) => {
                patchState(store, {
                  codice_operatore: undefined,
                  codice_operatore_loading: false,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
        }
      });

      effect(() => {
        const user = store.user_data?.();
        if (user) {
          patchState(store, { privacy_loading: true });
          _getterPrivacy
            .fetch(
              {},
              {
                context: {
                  headers: {
                    'x-hasura-role': 'user',
                  },
                },
              },
            )
            .subscribe({
              next: (res) => {
                patchState(store, {
                  privacy_data: res.data?.admin_consenso_privacy,
                  privacy_loading: false,
                });
              },
              error: (error) => {
                patchState(store, {
                  privacy_data: undefined,
                  privacy_loading: false,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
        }
      });

      effect(() => {
        const user = store.user_data?.();
        if (user) {
          patchState(store, {
            prosumer_profile_loading: true,
            installatore_profile_loading: true,
            founder_profile_loading: true,
          });
          _getterProfile
            .fetch(
              {},
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .subscribe({
              next: (res) => {
                patchState(store, {
                  prosumer_profile_data:
                    res.data?.profilo?.find(
                      (el) => el?.tipologia == _Tipologia_Profilo_Enum.Prosumer,
                    ) ?? 'NO',
                  prosumer_profile_loading: false,
                  installatore_profile_data:
                    res.data?.profilo?.find(
                      (el) =>
                        el?.tipologia == _Tipologia_Profilo_Enum.Installatore,
                    ) ?? 'NO',
                  installatore_profile_loading: false,
                  founder_profile_data:
                    res.data?.profilo?.find(
                      (el) => el?.tipologia == _Tipologia_Profilo_Enum.Founder,
                    ) ?? 'NO',
                  founder_profile_loading: false,
                });
              },
              error: (error) => {
                patchState(store, {
                  prosumer_profile_data: undefined,
                  prosumer_profile_loading: false,
                  installatore_profile_data: undefined,
                  installatore_profile_loading: false,
                  founder_profile_data: undefined,
                  founder_profile_loading: false,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
        }
      });
    },
  }),
);
